import { Archive, ArrowLeft, Copy, ExternalLink, Globe, Layers, MoreHorizontal } from "lucide-react";
import {
  AppLink,
  Avatar,
  Button,
  ClickPopover,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuRoot,
  MenuSeparator,
  MenuTrigger,
  StatusPopover,
  copyText,
  cx,
  formatBytes,
  formatDateTime,
  useToast,
} from "@amber/ui";
import { DateConflictList, ProgressBar, formatSourceDate } from "@amber/collection/ui";
import type { EvidenceRecord, SpeakerView, TurnView } from "../../contracts/index";
import { clock, languageName } from "../format";
import type { EvidenceSearch } from "../search";
import { CopiesMenuItems, copiesButtonLabel, type CopyAction } from "../copies/Copies";
import { FailureContent, ReadinessContent, SpeakerContent } from "./Popovers";

export type RecordRole = "viewer" | "editor" | "operator" | "admin";
export type RecordSearchHandler = (next: EvidenceSearch, version?: number | null) => void;

const BACK: Record<string, { label: string; href: string }> = {
  backlog: { label: "Backlog", href: "/backlog" },
  tasks: { label: "Tasks", href: "/tasks" },
  search: { label: "Search", href: "/search" },
};

function PersonChip({ speaker, onSeek }: { speaker: SpeakerView; onSeek: (ms: number) => void }) {
  return (
    <ClickPopover
      label={`${speaker.display}, ${speaker.kind_label}`}
      className="inline-flex h-7 items-center gap-1.5 rounded-full border border-divider bg-bg py-0.5 pr-2.5 pl-0.5 text-md hover:bg-surface-3"
      trigger={
        <>
          <Avatar name={speaker.display} muted={speaker.kind !== "tracked"} />
          <span className="font-strong">{speaker.display}</span>
          {speaker.kind !== "tracked" ? <span className="text-muted">· {speaker.kind_label}</span> : null}
        </>
      }
    >
      <SpeakerContent speaker={speaker} onSeek={onSeek} />
    </ClickPopover>
  );
}

function dateLine(record: EvidenceRecord): string[] {
  const d = record.item.dates;
  const event = d.event?.basis === "publication_proxy" ? null : d.event;
  const out: string[] = [];
  if (event) out.push(`Occurred ${formatSourceDate(event)}`);
  else if (d.published) out.push("Occurred unknown");
  if (d.published) out.push(`Published ${formatSourceDate(d.published)}${event ? "" : " (used as date)"}`);
  return out;
}

function passageQuery(turn: TurnView | undefined): string {
  if (!turn) return "";
  if (turn.locator) return `?p=${String(turn.locator.paragraphs[0])}`;
  return `?t=${String(Math.floor(turn.start_ms / 1000))}`;
}

interface HeaderActions {
  onRestrict: () => void;
  onRestore: () => void;
  onReport: () => void;
  onCopy: (a: CopyAction) => void;
  onDetails: () => void;
  onSplit: () => void;
  onMerge: () => void;
  onTiming: () => void;
  onCheck: () => void;
}

export function RecordHeader({
  record,
  search,
  role,
  onSearch,
  onSeek,
  selectedTurn,
  actions,
}: {
  record: EvidenceRecord;
  search: EvidenceSearch;
  role: RecordRole;
  onSearch: RecordSearchHandler;
  onSeek: (ms: number) => void;
  selectedTurn: number | null;
  actions: HeaderActions;
}) {
  return (
    <header className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-6 gap-y-1 px-4 pt-3 pb-3 max-compact:grid-cols-1 max-compact:px-2">
      <RecordTitle record={record} search={search} onSeek={onSeek} />
      <div className="flex flex-col items-end gap-2 max-compact:items-start">
        <RecordActions
          record={record}
          search={search}
          role={role}
          onSearch={onSearch}
          selectedTurn={selectedTurn}
          actions={actions}
        />
        <RecordStatus record={record} />
        <Provenance record={record} />
      </div>
    </header>
  );
}

function RecordTitle({
  record,
  search,
  onSeek,
}: {
  record: EvidenceRecord;
  search: EvidenceSearch;
  onSeek: (ms: number) => void;
}) {
  const back = BACK[search.from ?? "backlog"] ?? { label: "Backlog", href: "/backlog" };
  const tracked = record.speakers.filter((s) => s.kind === "tracked");
  const others = record.speakers.filter((s) => s.name && s.kind !== "tracked");
  const unresolved = record.speakers.filter((s) => !s.name).length;
  const meta = [
    record.item.platform.label,
    record.item.channel,
    record.item.type_label,
    ...dateLine(record),
    languageName(record.item.language),
    record.item.duration_ms === null ? null : clock(record.item.duration_ms),
  ].filter((v): v is string => Boolean(v));
  return (
    <div className="min-w-0">
      <div className="mb-1 flex items-center gap-2 text-md">
        <AppLink href={back.href} className="inline-flex items-center gap-1 font-strong text-link">
          <ArrowLeft size={14} aria-hidden="true" />
          {back.label}
        </AppLink>
        <span className="text-muted">Evidence record</span>
      </div>
      <h1 className="m-0 text-2xl font-semibold">{record.item.title}</h1>
      <p className="m-0 mt-0.5 flex flex-wrap gap-x-2 text-md text-muted">
        {meta.map((m, i) => (
          <span key={`${m}-${String(i)}`}>
            {i > 0 ? <span aria-hidden="true">· </span> : null}
            {m}
          </span>
        ))}
      </p>
      <DateConflictList conflicts={record.item.dates.conflicts} className="mt-0.5 text-md" />
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {tracked.map((s) => (
          <PersonChip key={s.id} speaker={s} onSeek={onSeek} />
        ))}
        {others.slice(0, 2).map((s) => (
          <PersonChip key={s.id} speaker={s} onSeek={onSeek} />
        ))}
        {others.length > 2 ? (
          <span className="text-md text-muted">+{others.length - 2} other named speakers</span>
        ) : null}
        {record.speakers.length > 0 ? (
          <span className="inline-flex h-7 items-center rounded-full border border-dashed border-divider px-2.5 text-md text-muted">
            {unresolved === 0
              ? "All speakers named"
              : `${String(unresolved)} unresolved speaker${unresolved === 1 ? "" : "s"}`}
          </span>
        ) : null}
        {record.communications.length > 1 ? <CommunicationsChip record={record} onSeek={onSeek} /> : null}
      </div>
    </div>
  );
}

function CommunicationsChip({ record, onSeek }: { record: EvidenceRecord; onSeek: (ms: number) => void }) {
  return (
    <ClickPopover
      label={`Contains ${String(record.communications.length)} communications`}
      trigger={
        <span className="inline-flex h-7 items-center gap-1 rounded-full border border-divider px-2.5 text-md font-strong">
          <Layers size={13} aria-hidden="true" />
          Contains {record.communications.length} communications
        </span>
      }
    >
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {record.communications.map((c) => (
          <li key={c.id}>
            <button
              type="button"
              className="text-left font-strong text-link"
              onClick={() => {
                onSeek(c.range_ms?.[0] ?? 0);
              }}
            >
              {c.title ?? `Communication ${String(c.ordinal)}`}
            </button>
            <div className="text-md text-muted">
              {c.range_ms ? `${clock(c.range_ms[0])}–${clock(c.range_ms[1])}` : "Whole item"} ·{" "}
              {c.readiness
                ? c.readiness.people
                    .map(
                      (p) => `${p.result === "eligible" ? "Eligible" : "Draft"} for ${p.name ?? "no tracked person"}`,
                    )
                    .join(" · ")
                : "Not assessed yet"}
            </div>
          </li>
        ))}
      </ul>
    </ClickPopover>
  );
}

function RecordActions({
  record,
  search,
  role,
  onSearch,
  selectedTurn,
  actions,
}: {
  record: EvidenceRecord;
  search: EvidenceSearch;
  role: RecordRole;
  onSearch: RecordSearchHandler;
  selectedTurn: number | null;
  actions: HeaderActions;
}) {
  const toast = useToast();
  const version = record.pinned?.version ?? record.communications[0]?.version?.version ?? null;
  const link = `${window.location.origin}/items/${record.item.id}${version ? `@${String(version)}` : ""}${passageQuery(record.turns.find((t) => t.idx === selectedTurn))}`;
  return (
    <div className="flex items-center gap-1.5">
      <MenuRoot>
        <MenuTrigger asChild>
          <Button size="sm" className="max-w-[340px]">
            <Copy size={13} aria-hidden="true" />
            <span className="truncate">{copiesButtonLabel(record)}</span>
          </Button>
        </MenuTrigger>
        <MenuContent className="w-[380px] max-w-[calc(100vw-16px)]">
          <CopiesMenuItems record={record} onAction={actions.onCopy} />
        </MenuContent>
      </MenuRoot>
      <Button
        size="sm"
        onClick={() => {
          void copyText(link).then((ok) => {
            toast.show(ok ? "Link copied" : "Couldn't copy the link");
          });
        }}
      >
        Copy link
      </Button>
      <MoreActionsMenu
        record={record}
        search={search}
        role={role}
        onSearch={onSearch}
        selectedTurn={selectedTurn}
        actions={actions}
      />
    </div>
  );
}

function MoreActionsMenu({
  record,
  search,
  role,
  onSearch,
  selectedTurn,
  actions,
}: {
  record: EvidenceRecord;
  search: EvidenceSearch;
  role: RecordRole;
  onSearch: RecordSearchHandler;
  selectedTurn: number | null;
  actions: HeaderActions;
}) {
  const { onRestrict, onRestore, onReport } = actions;
  return (
    <MenuRoot>
      <MenuTrigger asChild>
        <Button size="sm" aria-label="More actions">
          <MoreHorizontal size={15} aria-hidden="true" />
        </Button>
      </MenuTrigger>
      <MenuContent>
        <MenuItem
          onSelect={() => {
            onSearch({ ...search, tab: "history" });
          }}
        >
          Older versions
        </MenuItem>
        <MenuItem asChild>
          <AppLink href={`/backlog/${record.item.id}`}>Show in backlog</AppLink>
        </MenuItem>
        <MenuSeparator />
        <CorrectionMenuItems record={record} selectedTurn={selectedTurn} actions={actions} />
        <MenuItem
          disabled={!record.actions.check.enabled || record.pinned !== null}
          onSelect={actions.onCheck}
          title={record.actions.check.enabled ? undefined : record.actions.check.reason}
        >
          Request passage check…
        </MenuItem>
        {!record.actions.check.enabled ? (
          <MenuLabel className="max-w-[240px] font-normal whitespace-normal">{record.actions.check.reason}</MenuLabel>
        ) : null}
        <MenuItem
          disabled={!record.actions.report.enabled || record.pinned !== null}
          onSelect={onReport}
          title={record.actions.report.enabled ? undefined : record.actions.report.reason}
        >
          Report a problem…
        </MenuItem>
        {!record.actions.report.enabled ? (
          <MenuLabel className="max-w-[240px] font-normal whitespace-normal">{record.actions.report.reason}</MenuLabel>
        ) : null}
        <MenuSeparator />
        <AccessMenuItems record={record} role={role} onRestrict={onRestrict} onRestore={onRestore} />
      </MenuContent>
    </MenuRoot>
  );
}

function CorrectionMenuItems({
  record,
  selectedTurn,
  actions,
}: {
  record: EvidenceRecord;
  selectedTurn: number | null;
  actions: HeaderActions;
}) {
  const turn = record.turns.find((t) => t.idx === selectedTurn);
  const correctable = record.actions.correct.enabled && record.pinned === null;
  const mergeFrom = record.communications.find((c) => c.ordinal === (turn?.communication ?? 1));
  const mergeable =
    mergeFrom && record.communications.some((c) => c.ordinal > mergeFrom.ordinal)
      ? (mergeFrom.title ?? `communication ${String(mergeFrom.ordinal)}`)
      : null;
  return (
    <>
      <MenuItem
        disabled={!correctable}
        onSelect={actions.onDetails}
        title={record.actions.correct.enabled ? undefined : record.actions.correct.reason}
      >
        Correct record details…
      </MenuItem>
      <MenuItem
        disabled={!record.actions.timing.enabled || record.pinned !== null}
        onSelect={actions.onTiming}
        title={record.actions.timing.enabled ? undefined : record.actions.timing.reason}
      >
        Adjust timing
      </MenuItem>
      <MenuItem
        disabled={!correctable || !turn || turn.n === 1}
        onSelect={actions.onSplit}
        title={turn ? undefined : "Select the turn that starts the second part first"}
      >
        Split communication before {turn ? `turn ${String(turn.n)}` : "the selected turn"}…
      </MenuItem>
      <MenuItem
        disabled={!correctable || mergeable === null}
        onSelect={actions.onMerge}
        title={mergeable === null ? "This is the last communication in the item" : undefined}
      >
        Merge {mergeable ?? "communication"} with the next one…
      </MenuItem>
    </>
  );
}

function AccessMenuItems({
  record,
  role,
  onRestrict,
  onRestore,
}: {
  record: EvidenceRecord;
  role: RecordRole;
  onRestrict: () => void;
  onRestore: () => void;
}) {
  return (
    <>
      {record.restriction ? (
        <MenuItem disabled={!record.actions.restore.enabled} onSelect={onRestore}>
          {record.restriction.state === "deleted" ? "Restore…" : "Lift restriction…"}
        </MenuItem>
      ) : (
        <MenuItem
          disabled={!record.actions.restrict.enabled}
          onSelect={onRestrict}
          title={record.actions.restrict.enabled ? undefined : record.actions.restrict.reason}
        >
          Restrict or delete…
        </MenuItem>
      )}
      {!record.actions.restrict.enabled && role !== "admin" ? (
        <MenuLabel className="max-w-[240px] font-normal whitespace-normal">{record.actions.restrict.reason}</MenuLabel>
      ) : null}
    </>
  );
}

function RecordStatus({ record }: { record: EvidenceRecord }) {
  const finished = record.status.status === "finished";
  return (
    <div className="flex items-center gap-2.5">
      <div className="w-[130px]">
        <ProgressBar item={record} />
      </div>
      {record.failure ? null : <span className="text-ui">{record.status.label}</span>}
      {finished && record.readiness ? (
        <StatusPopover
          label={
            <span
              className={cx(
                "rounded-full border px-2 text-md font-strong",
                record.readiness.result === "eligible" ? "border-done text-fg" : "border-border text-fg",
              )}
            >
              {record.readiness.result === "eligible" ? "Eligible" : "Draft"}
            </span>
          }
          accessibleLabel={`Readiness: ${record.readiness.result === "eligible" ? "Eligible" : "Draft"}`}
        >
          <ReadinessContent record={record} />
        </StatusPopover>
      ) : null}
      {record.failure ? (
        <StatusPopover
          label={<span className="text-md font-strong text-attention">{record.failure.title}</span>}
          accessibleLabel={record.failure.title}
        >
          <FailureContent record={record} />
        </StatusPopover>
      ) : null}
    </div>
  );
}

function Provenance({ record }: { record: EvidenceRecord }) {
  return (
    <div className="flex gap-2 max-[640px]:flex-col">
      <div className="flex items-start gap-2 rounded-lg border border-divider bg-bg px-2.5 py-1.5 text-md">
        <Globe size={13} aria-hidden="true" className="mt-0.5 text-muted" />
        <div>
          <div>
            <span className="font-strong">Original</span>{" "}
            <a
              href={record.provenance.original.url}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-0.5 text-link"
            >
              {record.provenance.original.host}
              <ExternalLink size={11} aria-hidden="true" />
            </a>
          </div>
          <div className="text-muted">
            {record.provenance.original.state}
            {record.provenance.original.checked_at
              ? `, checked ${formatDateTime(record.provenance.original.checked_at)}`
              : ""}
          </div>
        </div>
      </div>
      <div className="flex items-start gap-2 rounded-lg border border-divider bg-bg px-2.5 py-1.5 text-md">
        <Archive size={13} aria-hidden="true" className="mt-0.5 text-muted" />
        <div>
          <div className="font-strong">Archived copy</div>
          <div className="text-muted">
            {record.provenance.archived
              ? `captured ${formatDateTime(record.provenance.archived.captured_at)}${record.provenance.archived.size_bytes ? ` · ${formatBytes(record.provenance.archived.size_bytes)}` : ""}`
              : "None — nothing archived yet"}
          </div>
        </div>
      </div>
    </div>
  );
}
